import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { chatKeys, startChat } from "../../api/chat";
import { errorText } from "../../api/client";
import { useMe } from "../../api/hooks";
import { Composer } from "../../components/chat/LazyComposer";
import { PhoneBell } from "../../components/shell/Notifications";
import { BackButton } from "../../components/shell/Page";
import { BrandMark } from "../../components/ui/primitives";
import { t } from "../../i18n";
import { newId } from "../../lib/secure";
import { useUi } from "../../state/ui";

export const Route = createFileRoute("/_app/")({
  // `q`: a question to pre-fill, not send (Ask privately, PLAN §8.4).
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q ? { q: search.q.slice(0, 4000) } : {},
  component: NewChat,
});

/** The starter questions, in the language shown (said at render, never at load). */
const starters = () => [t("chat.starter1"), t("chat.starter2"), t("chat.starter3")];

/** A new chat: nothing exists until the first message, which creates the conversation and
 *  opens it, in one request (R10-S12: a failed send leaves no empty chat behind). */
function NewChat() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const { q } = Route.useSearch();
  // The client_id belongs to the words being sent: sending the same ones again (the answer
  // to the first try was lost) gets the same chat back, not a second one.
  const draft = useRef<{ key: string; id: string } | null>(null);
  const [starting, setStarting] = useState(false);

  const onSend = async (text: string, fileIds: string[], _mentions?: unknown, deep?: boolean) => {
    const key = JSON.stringify([text, fileIds, Boolean(deep)]);
    if (draft.current?.key !== key) draft.current = { key, id: newId() };
    const started = await startChat({
      client_id: draft.current.id,
      body_md: text,
      attachments: fileIds,
      go_deep: deep ?? false,
    });
    draft.current = null;
    await qc.invalidateQueries({ queryKey: chatKeys.conversations });
    await navigate({ to: "/c/$id", params: { id: started.conversation.id } });
  };
  // A starter is one tap: a second while the first goes changes nothing (R10-S6).
  const start = (text: string) => {
    if (starting) return;
    setStarting(true);
    onSend(text, [])
      .catch((e: unknown) => toast(errorText(e), true))
      .finally(() => setStarting(false));
  };

  return (
    // R7.5: the page is the shell's main area (the list column beside it is the "Chat" region).
    <main className="sub main on no-panel" aria-label={t("chat.newChatAria")}>
      <div className="conv">
        <header className="conv-head">
          <BackButton />
          <div className="ttl">
            <h1>{t("chat.newChat")}</h1>
            <p>{t("chat.privateChat")}</p>
          </div>
          <PhoneBell />
        </header>
        <div className="feed scroll new-chat">
          <div className="empty">
            <BrandMark className="hero" />
            <h2>{t("chat.hi", { name: me.data?.called_name ?? "" })}</h2>
            <p>{t("chat.newChatIntro")}</p>
            <div className="starters">
              {starters().map((s) => (
                <button
                  key={s}
                  type="button"
                  className="fchipx"
                  disabled={starting}
                  onClick={() => start(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <Composer
          key={q ?? ""}
          draftKey="new-chat"
          label={t("chat.messageTo", { name: "Vantage" })}
          placeholder={t("chat.askAnything")}
          onSend={onSend}
          initialText={q}
          goDeep
        />
      </div>
    </main>
  );
}
